// src/api/brand/brandApi.ts
import { axiosInstance } from "src/api/axiosInstance";
import type { ApiResponse } from "src/api/common/apiTypes";

export type BrandFile = {
  fileId: number;
  originalName: string;
  savedName: string;
  fileUrl: string;
  fileSize: number;
  contentType: string;
  fileOrder: number;
};
export type BrandListItem = {
  id: number;

  brandCode?: string;
  brandCategoryId?: number;
  brandCategoryName?: string;

  brandNameKo: string;
  brandNameEn?: string;
  brandDescription?: string;

  brandLogoFileGrpId?: number;
  logoFiles?: BrandFile[];

  brandThumbnailFileGrpId?: number;
  thumbnailFiles?: BrandFile[];

  officialWebsite?: string;
  originCountry?: string;

  displayOrder?: number;
  isFeatured?: boolean;
  isActive?: boolean;

  createdAt?: string;
  updatedAt?: string;
};

export type BrandItem = BrandListItem & {
  // detail에 더 오는 게 있으면 여기에 추가
};

export type BrandListResponse = {
  content: BrandListItem[];
  page: {
    size: number;
    number: number; // 0-based
    totalElements: number;
    totalPages: number;
  };
};

export type GetBrandListParams = {
  brandName?: string;
  categoryId?: number;
  isActive?: boolean;
  isFeatured?: boolean;
  page: number; // 0-based
  size: number;
  order: string; // "displayOrder,asc"
};

export type BrandUpsertBody = {
  brandCode?: string;
  brandCategoryId?: number;

  brandNameKo: string;
  brandNameEn?: string;
  brandDescription?: string;

  logoFileGrpId?: number;
  thumbnailFileGrpId?: number;

  officialWebsite?: string;
  originCountry?: string;

  displayOrder?: number;
  isFeatured?: boolean;
  isActive?: boolean;
};

export type BrandCategoryItem = {
  id: number;
  categoryCode: string;
  categoryName: string;
};

export const getBrandCategoryListApi = async (): Promise<ApiResponse<BrandCategoryItem[]>> => {
  const res = await axiosInstance.get<ApiResponse<BrandCategoryItem[]>>(
    "/v1/admin/baseinfo/brand/category/list"
  );
  return res.data;
};

// LIST
export const getBrandListApi = async (
  params: GetBrandListParams
): Promise<ApiResponse<BrandListResponse>> => {
  const res = await axiosInstance.get<ApiResponse<BrandListResponse>>(
    "/v1/admin/baseinfo/brand/list",
    {
      params: {
        brandName: params.brandName || undefined,
        categoryId: params.categoryId ?? undefined,
        isActive: typeof params.isActive === "boolean" ? params.isActive : undefined,
        isFeatured: typeof params.isFeatured === "boolean" ? params.isFeatured : undefined,
        page: params.page,
        size: params.size,
        order: params.order,
      },
    }
  );
  return res.data;
};

// DETAIL
export const getBrandApi = async (id: number): Promise<ApiResponse<BrandItem>> => {
  const res = await axiosInstance.get<ApiResponse<BrandItem>>(`/v1/admin/baseinfo/brand/${id}`);
  return res.data;
};

// CREATE (JSON)
export const createBrandApi = async (payload: BrandUpsertBody): Promise<ApiResponse<BrandItem>> => {
  const res = await axiosInstance.post<ApiResponse<BrandItem>>("/v1/admin/baseinfo/brand", payload);
  return res.data;
};

// UPDATE (JSON)
export const updateBrandApi = async (
  id: number,
  payload: BrandUpsertBody
): Promise<ApiResponse<BrandItem>> => {
  const res = await axiosInstance.put<ApiResponse<BrandItem>>(
    `/v1/admin/baseinfo/brand/${id}`,
    payload
  );
  return res.data;
};

// DELETE
export const deleteBrandApi = async (id: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(`/v1/admin/baseinfo/brand/${id}`);
  return res.data;
};
